import React, { useState, useEffect } from 'react';
import { promptData } from '../../../data/data_prompts';
import './AdministrationScripts.css';
import ScriptForm from './ScriptForm';

function AdministrationScripts() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scriptToDelete, setScriptToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [scriptToClone, setScriptToClone] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneDescription, setCloneDescription] = useState('');
  const [cloning, setCloning] = useState(false);

  useEffect(() => {
    fetchScripts();
  }, []);

  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await promptData.getPromptScripts();
      if (response && response.success) {
        setScripts(response.promptScripts || []);
      } else {
        setError('Failed to fetch prompt scripts');
      }
    } catch (err) {
      console.error('Error fetching prompt scripts:', err);
      setError('Error loading prompt scripts');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (script) => {
    setEditingScript(script);
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingScript(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingScript(null);
  };

  const handleFormSubmit = async (scriptData) => {
    try {
      if (editingScript) {
        // Update existing script
        await promptData.updatePromptScript(
          editingScript.id,
          scriptData.name,
          scriptData.description,
          scriptData.script,
          scriptData.system_script || '',
          scriptData.active
        );
      } else {
        // Create new script
        await promptData.createPromptScript(
          scriptData.name,
          scriptData.description,
          scriptData.script,
          scriptData.system_script || ''
        );
      }
      
      // Refresh the scripts list
      await fetchScripts();
      handleFormClose();
    } catch (err) {
      console.error('Error saving script:', err);
      // You might want to show an error message to the user here
    }
  };

  const handleDeleteClick = (script) => {
    setScriptToDelete(script);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!scriptToDelete) return;

    try {
      setDeleting(true);
      const response = await promptData.deletePromptScript(scriptToDelete.id);
      
      if (response && response.success) {
        // Remove the deleted script from the list
        setScripts(scripts.filter(s => s.id !== scriptToDelete.id));
        setShowDeleteModal(false);
        setScriptToDelete(null);
      } else {
        setError('Failed to delete prompt script');
      }
    } catch (err) {
      console.error('Error deleting prompt script:', err);
      setError('Error deleting prompt script');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setScriptToDelete(null);
  };

  const handleCloneClick = (script) => {
    setScriptToClone(script);
    setCloneName(`${script.name} copy`);
    setCloneDescription(script.description || '');
    setShowCloneModal(true);
  };

  const handleCloneConfirm = async () => {
    if (!scriptToClone || !cloneName.trim()) return;

    try {
      setCloning(true);
      const response = await promptData.clonePromptScript(scriptToClone.id, cloneName.trim(), cloneDescription.trim());
      
      if (response && response.success) {
        // Refresh the scripts list to show the new cloned script
        await fetchScripts();
        setShowCloneModal(false);
        setScriptToClone(null);
        setCloneName('');
        setCloneDescription('');
      } else {
        setError('Failed to clone prompt script');
      }
    } catch (err) {
      console.error('Error cloning prompt script:', err);
      setError('Error cloning prompt script');
    } finally {
      setCloning(false);
    }
  };

  const handleCloneCancel = () => {
    setShowCloneModal(false);
    setScriptToClone(null);
    setCloneName('');
    setCloneDescription('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="scripts-container">
        <div className="scripts-header">
          <h1>Prompt Scripts</h1>
        </div>
        <div className="loading-state">
          <i className="ti ti-loader ti-spin"></i>
          <p>Loading prompt scripts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scripts-container">
        <div className="scripts-header">
          <h1>Prompt Scripts</h1>
        </div>
        <div className="error-state">
          <i className="ti ti-alert-circle"></i>
          <p>{error}</p>
          <button onClick={fetchScripts} className="retry-button">
            <i className="ti ti-refresh"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="scripts-container">
      <div className="scripts-header">
        <h1>Prompt Scripts</h1>
        <p>Manage prompt scripts for the AI coaching system</p>
      </div>
      
      <div className="scripts-actions">
        <button onClick={handleCreate} className="create-button">
          <i className="ti ti-plus"></i>
          Create New Script
        </button>
      </div>
      
      <div className="scripts-table-container">
        <table className="scripts-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created</th>
              <th className="action-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            {scripts.map((script) => (
              <tr key={script.id}>
                <td className="script-id">{script.id}</td>
                <td className="script-name">{script.name}</td>
                <td className="script-description" title={script.description || ''}>
                  <div className="description-text">
                    {script.description || ''}
                  </div>
                </td>
                <td className="script-status">
                  <span className={`status-badge ${script.active ? 'active' : 'inactive'}`}>
                    {script.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="script-created">{formatDate(script.created_at)}</td>
                <td className="action-column">
                  <button 
                    onClick={() => handleEdit(script)}
                    className="edit-button"
                    title="Edit script"
                  >
                    <i className="ti ti-edit"></i>
                  </button>
                  <button 
                    onClick={() => handleCloneClick(script)}
                    className="clone-button"
                    title="Clone script"
                  >
                    <i className="ti ti-copy"></i>
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(script)}
                    className="delete-button"
                    title="Delete script"
                  >
                    <i className="ti ti-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {scripts.length === 0 && (
          <div className="empty-state">
            <i className="ti ti-file-code"></i>
            <p>No prompt scripts found</p>
            <button onClick={handleCreate} className="create-first-button">
              <i className="ti ti-plus"></i>
              Create Your First Script
            </button>
          </div>
        )}
      </div>

      {showForm && (
        <ScriptForm
          script={editingScript}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Delete Prompt Script</h3>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete the prompt script "<strong>{scriptToDelete?.name}</strong>"?
              </p>
              <p className="warning-text">
                This action cannot be undone. The script will be permanently removed.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleDeleteCancel} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm} 
                className="delete-confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showCloneModal && (
        <div className="modal-overlay">
          <div className="clone-modal">
            <div className="modal-header">
              <h3>Clone Prompt Script</h3>
            </div>
            <div className="modal-content">
              <p>
                Create a copy of the prompt script "<strong>{scriptToClone?.name}</strong>"
              </p>
              <div className="form-group">
                <label htmlFor="clone-name">New Name:</label>
                <input
                  type="text"
                  id="clone-name"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  placeholder="Enter new name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="clone-description">Description:</label>
                <textarea
                  id="clone-description"
                  value={cloneDescription}
                  onChange={(e) => setCloneDescription(e.target.value)}
                  placeholder="Enter description"
                  className="form-input"
                  rows="3"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleCloneCancel} 
                className="cancel-button"
                disabled={cloning}
              >
                Cancel
              </button>
              <button 
                onClick={handleCloneConfirm} 
                className="clone-confirm-button"
                disabled={cloning || !cloneName.trim()}
              >
                {cloning ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Cloning...
                  </>
                ) : (
                  <>
                    <i className="ti ti-copy"></i>
                    Clone
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdministrationScripts; 